import { Autocomplete } from '@mui/material';
import { L } from 'harmony-language';
import React from 'react';
import { ValidatedTextField, Validation } from './inputs/validated-text-field';
import { CustomPopper } from './multi-select/custom-popper';

export interface AgisticsItem {
    id: number;
    value: string;
}

interface AgisticsSelectProps {
    item: number | null;
    items: AgisticsItem[];
    onChange: (n: number) => void;
    label?: string;
    blurOnSelect?: boolean;
    confinedSpace?: boolean;
    customPopper?: boolean;
    disabled?: boolean;
    showNone?: boolean;
    validations?: Validation[];
    /**
     * Function that will return styling to be applied to selected value in `TextField` and options in popper.
     */
    styleForOptionsFn?: (optionId: number) => React.CSSProperties;
}

const noneItem = () => {
    return {
        id: -1,
        value: L.none(),
    }
}

export const AgisticsSelect: React.FC<AgisticsSelectProps> = (props) => {
    const {
        item,
        items,
        onChange,
        label,
        blurOnSelect = false,
        confinedSpace = false,
        customPopper = false,
        disabled = false,
        showNone = false,
        validations,
        styleForOptionsFn,
    } = props;

    const selectedValue = React.useMemo(() => {
        const val = items.find(x => x.id === item);
        return val ? val : noneItem();
    }, [item]);

    const newOptions: () => AgisticsItem[] = () => {
        if (showNone) {
            return [
                noneItem(),
                ...items,
            ];
        } else {
            return items;
        }
    }

    return (
        <Autocomplete
            disableClearable={true}
            disabled={disabled}
            blurOnSelect={blurOnSelect}
            options={newOptions()}
            getOptionLabel={(option) => option.value}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedValue}
            onChange={(event, value) => {
                if (event.type !== 'keydown' && value) {
                    onChange(value.id);
                }
            }}
            PopperComponent={customPopper ? CustomPopper : undefined}
            sx={confinedSpace ? { input: { fontSize: '.75rem' }} : undefined}
            renderInput={(params) => {
                const additionalInputProps = {
                    ...params.inputProps,
                    ...(styleForOptionsFn && { style: styleForOptionsFn(selectedValue.id) }),
                }

                return (
                    <ValidatedTextField
                        {...params}
                        label={label}
                        validations={validations}
                        inputProps={additionalInputProps}
                    />
                );
            }}
            renderOption={(props, option) => {
                return (
                    <li style={styleForOptionsFn && styleForOptionsFn(option.id)}
                        {...props}
                        // props passes a key that is value, which might not be unique (users/contacts with same name)
                        key={option.id}
                    >
                        {option.value}
                    </li>
                );
            }}
            slotProps={{
                popper: {
                    // Helps colors on location popper from overlapping with selected colors
                    placement: 'bottom-start',
                }
            }}
        />
    );
};

export function createAgisticsSelectElements<T extends {id: number}>(elements: T[], labelFunction: (element: T) => string): AgisticsItem[] {
    return elements.map((element) => ({
        id: element.id,
        value: labelFunction(element),
    }));
}